import React, {Dispatch} from "react";
import {get} from "lodash";
import {Column, EnrichedTable, SortBy} from "common/component/EnrichedTable";
import {
    AtRiskReasonMap,
    AuthorizationBillItem,
    AuthorizationBillItemChargeEntry,
    OfferedHoursStatus,
    OfferedHoursStatusMap,
    SortOrder
} from "../../domain";
import {formatDate, formatDateStr, formatLocalDateString, formatRangeDateStr} from "common/util/date";
import {AuthorizationBillingTabAction} from "./AuthorizationBillingTab";
import {LoadingSpinner} from "../../../../common";
import {parseISO} from "date-fns";


type AuthorizationBillingTableProps = {
    dispatch: Dispatch<AuthorizationBillingTabAction>;
    expandedGuids: string[];
    selectedGuids: string[];
    loading: boolean;
    data: AuthorizationBillItem[] | undefined;
    sortBy: SortBy<AuthorizationBillItem>;
}

const extractLastBillDate = (item: AuthorizationBillItem) => {
    if (item === null ||
        item.chargeEntries === null ||
        item.chargeEntries.length === 0) {
        return "";
    }
    let serviceDates = item.chargeEntries.map((entry) => entry.serviceDate);
    serviceDates.sort((a, b) => a.localeCompare(b));

    return formatDateStr(serviceDates[serviceDates.length - 1]);
}

export const AuthorizationBillingTable = ({
                                              dispatch,
                                              selectedGuids,
                                              expandedGuids,
                                              data,
                                              loading,
                                              sortBy
                                          }: AuthorizationBillingTableProps) => {
    const isItemQualifyForSelect = (item: AuthorizationBillItem) => OfferedHoursStatusMap.get(item.offeredHoursStatus) === OfferedHoursStatus.At_Risk ||
        (OfferedHoursStatusMap.get(item.offeredHoursStatus) === OfferedHoursStatus.Good_Standing && (item.atRiskResponseReasons?.length ?? 0) > 0)
    const itemNumQualifyForSelect = data?.filter((i) => isItemQualifyForSelect(i)).length ?? 0
    const isAllSelected = () =>
        (itemNumQualifyForSelect !== 0 &&
            data?.every(v => selectedGuids.includes(v.guid))) ?? false;

    return (
        <EnrichedTable<AuthorizationBillItem>
            sortBy={sortBy}
            setSortBy={(newSortColumn: Column<AuthorizationBillItem>) => {
                // sort ignore
                if (newSortColumn.attr === "_lastBillingDate" ||
                    newSortColumn.attr === "_unitsToMeetFullOffer") {
                    return;
                }

                let newSortOrder = SortOrder.DESC;
                if (newSortColumn.attr === sortBy.column && sortBy.order === newSortOrder) {
                    newSortOrder = SortOrder.ASC;
                }

                dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn.attr, order: newSortOrder}});
            }}
            styleOverride={{
                customTableStyle: {height: "calc(100vh - 68px - 100px - 37px - 90px - 60px - 36px)"}
            }}
            columns={[
                {name: 'Month/Period', attr: 'startDate', span: 6},
                {name: "Procedure Code", attr: 'procedureCode', span: 3},
                {name: "Full Offer Required Hrs", attr: 'directCareHoursRequired', span: 5},
                {name: "Direct Hrs Billed", attr: 'hoursBilled', span: 3},
                {name: "Direct Hrs to Meet Full Offer", attr: '_unitsToMeetFullOffer', span: 6},
                {name: "Last Billed Date", attr: '_lastBillingDate', span: 3},
            ]}
            rowHighlightFeat={{
                shouldHighlight: (item) => {
                    return item.offeredHoursStatus !== null && isItemQualifyForSelect(item);
                },
                highlightColorByItem: (item: AuthorizationBillItem) => {
                    if (OfferedHoursStatusMap.get(item.offeredHoursStatus) === OfferedHoursStatus.Good_Standing) {
                        return {
                            row: "bg-green-200 border-green-300 hover:bg-green-300",
                            column: "border-b border-green-300"
                        };
                    }

                    return {
                        row: "bg-red-200 border-red-300 hover:bg-red-300",
                        column: "border-b border-red-300"
                    };
                },
                highlightCss: {
                    row: "bg-red-200 border-red-300 hover:bg-red-300",
                    column: "border-b border-red-300"
                }
            }}
            rowsData={data}
            keyColumn={"guid"}
            cellDisplayFunc={(item, column) => {
                if (column.attr === "procedureCode") {
                    return !!item.modifier? item.procedureCode + ',' + item.modifier : item.procedureCode;
                }
                if (column.attr === "startDate") {
                    return formatRangeDateStr(item.startDate, item.endDate);
                }
                if (column.attr === "_lastBillingDate") {
                    return extractLastBillDate(item);
                }
                if (column.attr === "_unitsToMeetFullOffer") {
                    const missingHours = (item.directCareHoursRequired || 0) - (item.hoursBilled || 0);
                    return missingHours > 0 ? missingHours : 0;
                }

                return get(item, column.attr);
            }}
            multiselectFeat={{
                isSelected: (item) => selectedGuids.includes(item.guid),
                toggleSelect: (item) => dispatch({type: "SET_MULTI_SELECT_TOGGLE", item: item}),
                shouldDisableSelected: (item) => !isItemQualifyForSelect(item),
                toggleAllFeat: {
                    isAllSelected: () => isAllSelected(),
                    isSomeSelected: () => {
                        return (!isAllSelected() && data?.some(v => selectedGuids.includes(v.guid))) ?? false
                    },
                    toggle: () => dispatch({
                        type: "SET_MULTI_SELECT_TOGGLE_ALL",
                        items: data?.filter((item) => isItemQualifyForSelect(item)) ?? []
                    })
                }
            }}
            expandFeat={{
                itemHaveExpand: (item) => (item.chargeEntries !== null && item.chargeEntries?.length > 0) ||
                    (item.atRiskResponseReasons !== null && item.atRiskResponseReasons?.length > 0),
                shouldShowExpanded: (item) => expandedGuids.includes(item.guid),
                setExpandedFunc: (item) => dispatch({type: "SET_EXPAND_TOGGLE", item: item}),
                expandDisplayFunc: (item) =>
                    <div className="flex">
                        {
                            item.chargeEntries !== null && item.chargeEntries.length > 0 &&
                            <div className="w-8/12">
                                <EnrichedTable<AuthorizationBillItemChargeEntry>
                                    styleOverride={{
                                        tableContent: "overflow-auto flex flex-col flex-grow m-4 border border-gray-400",
                                        trHeader: 'text-left select-none text-xs text-gray-portal-table-title',
                                        trRow: 'border-b border-gray-200 relative hover:bg-gray-200 text-sm',
                                    }}
                                    columns={[
                                        {name: "Service Date", attr: "serviceDate", span: 3},
                                        {name: "Practitioner Name", attr: "practitionerName", span: 3},
                                        {name: "Direct Hours Billed", attr: "hoursBilled", span: 3},
                                    ]}
                                    cellDisplayFunc={(childItem, column) => get(childItem, column.attr)}
                                    rowsData={item.chargeEntries ?? []}
                                    keyColumn={"guid"}
                                    firstColumn={"serviceDate"}
                                    emptyMessage={<></>}
                                    hideOnEmpty
                                />
                            </div>
                        }
                        {
                            <AtRiskReasonsRender item={item}/>
                        }
                    </div>
            }}
            loadingFeat={{
                loading: loading,
                loadingDisplayElement: <LoadingSpinner spacing={20} height={"40vh"}/>
            }}

            emptyMessage={<div className='w-full flex items-center'>
                <div className='m-auto pt-8'>
                    There are no authorization billing information
                </div>
            </div>}
        />
    )
}

type AtRiskReasonsRenderProps = {
    item: AuthorizationBillItem
}
const AtRiskReasonsRender = ({item}: AtRiskReasonsRenderProps) => {
    if (item.atRiskResponseReasons === null || item.atRiskResponseReasons.length === 0) {
        return <div/>
    }
    const isStaffing = item.atRiskResponseReasons.length === 1 && item.atRiskResponseReasons[0] === 'Staffing';
    return (
        <div
            className={item.chargeEntries !== null && item.chargeEntries.length > 0 ? "w-4/12" : "w-full"}>
            <div className="ml-4 p-2 text-sm">
                <div className="font-bold pb-1">At Risk Response Reasons Submitted:</div>
                <ul className="list-disc list-inside pb-1">
                    {item.atRiskResponseReasons && item.atRiskResponseReasons.map((reason) =>
                        <li key={reason}>
                            {AtRiskReasonMap.get(reason)}
                        </li>
                    )}
                </ul>
                {item.staffingPlan != null && (<>
                    <div className="font-bold pb-1">Staffing Plan:</div>
                    <p>
                        Comments: {item.staffingPlan?.comments}
                    </p>
                    <p>
                        Full Offer Start Date: {formatDate(parseISO(item.staffingPlan?.fullOfferStartDate))}
                    </p>
                    <p>
                        Reviewed By Client
                        Family: {formatDate(parseISO(item.staffingPlan?.reviewedAndApprovedByClientFamilyDate))}
                    </p>
                    <p>
                        Paraprofessional Hours: {item.staffingPlan?.offeredParaprofessionalHours}
                    </p>
                    <p>
                        Mid-level Hours: {item.staffingPlan?.offeredMidLevelHours}
                    </p>
                    <p>
                        High-level Hours: {item.staffingPlan?.offeredHighLevelHours}
                    </p>
                    <p>
                        Group Care Hours: {item.staffingPlan?.offeredGroupCareHours}
                    </p>
                </>)}
                {item.submitter && (
                    <div className={"italic pt-2"}>At Risk Response Submitted by <span
                        className="font-bold">{`${item.submitter.firstName} ${item.submitter.lastName}`}</span> on
                        <span className="font-bold"> {formatLocalDateString(item.atRiskResponseDate)}</span>
                    </div>
                )}
                {
                    item.submittedAuthReviewedDate && (
                        <div>
                            <span className={"italic text-xs text-red-700 pt-1"}>Submitted Auth Reviewed Date:</span>
                            <span className="font-bold text-xs text-red-700"> {formatLocalDateString(item.submittedAuthReviewedDate)}</span>
                        </div>
                    )
                }
                {isStaffing &&
                    <>
                        <div>
                            <span className={"italic pt-2"}>Staffing Plan/Addendum Due Date: </span>
                            <span className="font-bold">{formatLocalDateString(item.staffingAddendumDueDate)}</span>
                        </div>
                        <div>
                            <span className={"italic pt-2"}>Staffing Plan/Addendum Submitted Date: </span>
                            <span className="font-bold">{formatLocalDateString(item.staffingAddendumSubmittedDate)}</span>
                        </div>
                    </>
                }
            </div>
        </div>
    )
}
